import React from 'react'
import Agilite from 'agilite'
import { message, notification } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWifi,
  faExclamationTriangle,
  faTimesCircle,
  faInfoCircle,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons'
import LibEnums from './enums'
import Store from '../../store'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

// Error Types Classification
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  UNKNOWN: 'UNKNOWN',
  BUSINESS_LOGIC: 'BUSINESS_LOGIC'
}

// Error Severity Levels
export const ERROR_SEVERITY = {
  LOW: 'LOW', // Non-critical, user can continue
  MEDIUM: 'MEDIUM', // Important but not blocking
  HIGH: 'HIGH', // Blocks current action
  CRITICAL: 'CRITICAL' // Blocks entire application
}

// User-friendly error messages with actions
const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: {
    title: 'Connection Issue',
    message: 'Unable to connect to our servers. Please check your internet connection.',
    icon: faWifi,
    actions: ['Retry', 'Check Connection'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.AUTHENTICATION]: {
    title: 'Authentication Required',
    message: 'Your session has expired. Please log in again.',
    icon: faShieldAlt,
    actions: ['Login Again'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.AUTHORIZATION]: {
    title: 'Access Denied',
    message: "You don't have permission to perform this action.",
    icon: faTimesCircle,
    actions: ['Contact Support'],
    severity: ERROR_SEVERITY.MEDIUM
  },
  [ERROR_TYPES.VALIDATION]: {
    title: 'Invalid Information',
    message: 'Please check the information you entered and try again.',
    icon: faExclamationTriangle,
    actions: ['Review Form'],
    severity: ERROR_SEVERITY.LOW
  },
  [ERROR_TYPES.SERVER]: {
    title: 'Server Error',
    message: 'Our servers are experiencing issues. Please try again in a moment.',
    icon: faTimesCircle,
    actions: ['Retry', 'Contact Support'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.BUSINESS_LOGIC]: {
    title: 'Operation Not Allowed',
    message: 'This action cannot be completed due to business rules.',
    icon: faInfoCircle,
    actions: ['Review Requirements'],
    severity: ERROR_SEVERITY.MEDIUM
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: 'Unexpected Error',
    message: 'Something went wrong. Our team has been notified.',
    icon: faExclamationTriangle,
    actions: ['Retry', 'Contact Support'],
    severity: ERROR_SEVERITY.MEDIUM
  }
}

// Enhanced error logging
const logError = async (error, context = {}) => {
  try {
    const errorData = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message || 'Unknown error',
        stack: error.stack,
        name: error.name,
        code: error.code
      },
      context: {
        user: Store.getState().auth?.agiliteUser?.email || 'Anonymous',
        userAgent: navigator.userAgent,
        url: window.location.href,
        component: context.component,
        action: context.action,
        ...context
      },
      severity: classifyError(error).severity
    }

    await agilite.BatchLogging.createLogEntry(
      LibEnums.LOG_PROFILE_KEY,
      LibEnums.META_CLINIC,
      LibEnums.META_CLINIC_PORTAL,
      LibEnums.errorCodes[400],
      errorData.error.message,
      LibEnums.contentTypes.APPLICATION_JSON,
      errorData
    )
  } catch (loggingError) {
    console.error('Failed to log error:', loggingError)
  }
}

// Smart error classification
export const classifyError = (error) => {
  // Network errors
  if (
    !error.response &&
    (error.code === 'NETWORK_ERROR' ||
      error.message?.toLowerCase().includes('network') ||
      error.message?.toLowerCase().includes('fetch'))
  ) {
    return { type: ERROR_TYPES.NETWORK, ...ERROR_MESSAGES[ERROR_TYPES.NETWORK] }
  }

  // HTTP Response errors
  if (error.response) {
    const status = error.response.status

    switch (true) {
      case status === 401:
        return { type: ERROR_TYPES.AUTHENTICATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHENTICATION] }

      case status === 403:
        return { type: ERROR_TYPES.AUTHORIZATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHORIZATION] }

      case status >= 400 && status < 500:
        // Check if it's a validation error
        if (
          error.response.data?.validationErrors ||
          error.response.data?.message?.toLowerCase().includes('validation')
        ) {
          return { type: ERROR_TYPES.VALIDATION, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }
        }
        return { type: ERROR_TYPES.CLIENT, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }

      case status >= 500:
        return { type: ERROR_TYPES.SERVER, ...ERROR_MESSAGES[ERROR_TYPES.SERVER] }
    }
  }

  // Firebase Auth errors - handle specific codes
  if (error.code?.startsWith('auth/') || error.message?.includes('auth/')) {
    const authCode = error.code || error.message.match(/auth\/[a-z-]+/)?.[0]

    switch (authCode) {
      case 'auth/invalid-verification-code':
        return {
          type: ERROR_TYPES.VALIDATION,
          title: 'Invalid Verification Code',
          message: 'The verification code you entered is incorrect. Please check and try again.',
          icon: faExclamationTriangle,
          actions: ['Try Again'],
          severity: ERROR_SEVERITY.LOW
        }

      case 'auth/code-expired':
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          title: 'Verification Code Expired',
          message: 'The verification code has expired. Please request a new code.',
          icon: faShieldAlt,
          actions: ['Request New Code'],
          severity: ERROR_SEVERITY.MEDIUM
        }

      case 'auth/invalid-phone-number':
        return {
          type: ERROR_TYPES.VALIDATION,
          title: 'Invalid Phone Number',
          message: 'Please enter a valid phone number.',
          icon: faExclamationTriangle,
          actions: ['Check Phone Number'],
          severity: ERROR_SEVERITY.LOW
        }

      case 'auth/too-many-requests':
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          title: 'Too Many Attempts',
          message: 'Too many failed attempts. Please wait a few minutes before trying again.',
          icon: faShieldAlt,
          actions: ['Wait and Retry'],
          severity: ERROR_SEVERITY.MEDIUM
        }

      case 'auth/account-exists-with-different-credential':
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          title: 'Account Exists',
          message: 'An account already exists with this phone number using a different sign-in method.',
          icon: faShieldAlt,
          actions: ['Try Different Method'],
          severity: ERROR_SEVERITY.MEDIUM
        }

      case 'auth/user-disabled':
        return {
          type: ERROR_TYPES.AUTHORIZATION,
          title: 'Account Disabled',
          message: 'Your account has been disabled. Please contact support.',
          icon: faTimesCircle,
          actions: ['Contact Support'],
          severity: ERROR_SEVERITY.HIGH
        }

      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          title: 'Sign In Failed',
          message: 'Invalid credentials. Please check your information and try again.',
          icon: faShieldAlt,
          actions: ['Try Again', 'Reset Password'],
          severity: ERROR_SEVERITY.MEDIUM
        }

      case 'auth/network-request-failed':
        return {
          type: ERROR_TYPES.NETWORK,
          title: 'Connection Issue',
          message: 'Unable to connect. Please check your internet connection and try again.',
          icon: faWifi,
          actions: ['Check Connection', 'Retry'],
          severity: ERROR_SEVERITY.HIGH
        }

      default:
        // Fallback for other Firebase auth errors
        return { type: ERROR_TYPES.AUTHENTICATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHENTICATION] }
    }
  }

  // Validation errors
  if (
    error.name === 'ValidationError' ||
    error.message?.toLowerCase().includes('validation') ||
    error.message?.toLowerCase().includes('required')
  ) {
    return { type: ERROR_TYPES.VALIDATION, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }
  }

  // Business logic errors
  if (
    error.message?.toLowerCase().includes('business') ||
    error.message?.toLowerCase().includes('rule') ||
    error.message?.toLowerCase().includes('not allowed')
  ) {
    return { type: ERROR_TYPES.BUSINESS_LOGIC, ...ERROR_MESSAGES[ERROR_TYPES.BUSINESS_LOGIC] }
  }

  // Default to unknown
  return { type: ERROR_TYPES.UNKNOWN, ...ERROR_MESSAGES[ERROR_TYPES.UNKNOWN] }
}

// Enhanced error handler with recovery options
export const handleError = (error, options = {}) => {
  const {
    showNotification = true,
    logError: shouldLog = true,
    context = {},
    customMessage = null,
    onRetry = null,
    component = 'Unknown'
  } = options

  // Classify the error
  const errorInfo = classifyError(error)

  // Log the error if needed
  if (shouldLog) {
    logError(error, { ...context, component })
  }

  // Use custom message if provided
  const displayMessage = customMessage || errorInfo.message

  console.error(`[${errorInfo.type}] ${component}:`, error)

  // Show appropriate notification based on severity
  if (showNotification) {
    const notificationConfig = {
      message: errorInfo.title,
      description: displayMessage,
      icon: <FontAwesomeIcon icon={errorInfo.icon} />,
      duration: errorInfo.severity === ERROR_SEVERITY.CRITICAL ? 0 : 4.5
    }

    // Add retry button if onRetry is provided
    if (onRetry && errorInfo.actions.includes('Retry')) {
      notificationConfig.btn = (
        <button
          onClick={() => {
            notification.close('error-notification')
            onRetry()
          }}
          style={{
            background: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 12px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      )
    }

    switch (errorInfo.severity) {
      case ERROR_SEVERITY.CRITICAL:
        notification.error({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.HIGH:
        notification.error({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.MEDIUM:
        notification.warning({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.LOW:
        message.warning(displayMessage)
        break
      default:
        notification.info({ ...notificationConfig, key: 'error-notification' })
    }
  }

  return {
    type: errorInfo.type,
    message: displayMessage,
    severity: errorInfo.severity,
    actions: errorInfo.actions,
    originalError: error
  }
}

// Specific error handlers for common scenarios
export const handleNetworkError = (onRetry) => {
  return handleError(new Error('Network connection failed'), {
    context: { type: 'network' },
    onRetry
  })
}

export const handleValidationError = (fieldErrors) => {
  const message = Object.values(fieldErrors).join('. ')
  return handleError(new Error(message), {
    customMessage: `Please correct the following: ${message}`,
    context: { type: 'validation', fields: Object.keys(fieldErrors) }
  })
}

export const handleAPIError = (error, context = {}) => {
  return handleError(error, {
    context: { type: 'api', ...context },
    component: context.component || 'API'
  })
}

// React Error Boundary helper
export const createErrorBoundary = (fallbackComponent) => {
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props)
      this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
      handleError(error, {
        context: {
          type: 'react-boundary',
          errorInfo: errorInfo.componentStack
        },
        component: 'ErrorBoundary'
      })
    }

    render() {
      if (this.state.hasError) {
        return fallbackComponent ? (
          fallbackComponent(this.state.error)
        ) : (
          <div>Something went wrong. Please refresh the page.</div>
        )
      }

      return this.props.children
    }
  }

  return ErrorBoundary
}

// Hook for handling errors in components
export const useErrorHandler = (component = 'Component') => {
  const handleComponentError = (error, context = {}) => {
    return handleError(error, {
      ...context,
      component
    })
  }

  return { handleError: handleComponentError }
}

const errorHandlerModule = {
  handleError,
  handleNetworkError,
  handleValidationError,
  handleAPIError,
  classifyError,
  createErrorBoundary,
  useErrorHandler,
  ERROR_TYPES,
  ERROR_SEVERITY
}

export default errorHandlerModule
